import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const HeroSection = ({ onGetStarted, activeRegion = 'australia', onRegionChange }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('buy');
  const [searchQuery, setSearchQuery] = useState('');
  const { t, language } = useLanguage();

  const tabs = [
    { id: 'buy', label: t('buy'), icon: 'Home' },
    { id: 'rent', label: t('rent'), icon: 'Key' },
    { id: 'sell', label: t('sell'), icon: 'Tag' },
    { id: 'commercial', label: t('commercial'), icon: 'Building' },
    { id: 'off-market', label: t('offMarket'), icon: 'Eye' },
    { id: 'mortgage', label: language === 'bn' ? 'মর্টগেজ' : 'Mortgage', icon: 'Calculator' }
  ];

  const handleSearch = (e) => {
    e?.preventDefault();
    const region = activeRegion || 'australia';
    if (searchQuery?.trim()) {
      navigate(`/search?mode=${activeTab}&query=${encodeURIComponent(searchQuery)}&region=${region}`);
    } else {
      navigate(`/search?mode=${activeTab}&region=${region}`);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    const region = activeRegion || 'australia';
    if (tabId === 'mortgage') {
      navigate('/mortgage-check');
    } else if (tabId === 'sell') {
      navigate('/create-listing');
    } else {
      navigate(`/search?mode=${tabId}&region=${region}`);
    }
  };

  return (
    <section className="relative py-12 md:py-20 lg:py-28 overflow-hidden" style={{ background: 'transparent' }}>
      {/* Animated glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-80 h-80 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.08) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }} />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(123, 47, 255, 0.08) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }} />
        {/* Tech circles */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full" style={{
          border: '1px solid rgba(0, 212, 255, 0.05)'
        }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full" style={{
          border: '1px solid rgba(0, 212, 255, 0.03)'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center space-y-6 md:space-y-8 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full text-sm md:text-base font-medium" style={{
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            color: '#00D4FF'
          }}>
            <img
              src="/assets/images/logo_LL-1772022913346.jpeg"
              alt="ListingLens logo"
              className="w-8 h-8 rounded-md object-cover"
              style={{ boxShadow: '0 0 10px rgba(0, 212, 255, 0.5)' }}
            />
            <Icon name="Sparkles" size={16} color="#00D4FF" />
            <span>AI-POWERED PROPERTY SEARCH ENGINE</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" style={{
            fontFamily: "'Orbitron', sans-serif"
          }}>
            <span style={{ color: '#00D4FF', textShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>Listing</span>
            <span style={{ color: '#E8F4FF' }}> Lens</span>
          </h1>

          <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto" style={{ color: '#6B9EC4' }}>
            Listing Lens is an AI‑powered property and mortgage platform that puts Australia and the UAE under one smart lens, giving buyers, sellers and investors instant finance checks, AI‑enhanced listings, and on‑demand access to trusted agents, lawyers, stylists and photographers in a single, seamless experience.
          </p>

          {/* remove Region Stats block */}

          {/* Tabs */}
          <div className="rounded-xl p-2" style={{
            background: 'rgba(10, 22, 40, 0.8)',
            border: '1px solid rgba(0, 212, 255, 0.15)'
          }}>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabClick(tab?.id)}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg transition-all"
                  style={{
                    background: activeTab === tab?.id ? 'rgba(0, 212, 255, 0.2)' : 'transparent',
                    color: activeTab === tab?.id ? '#00D4FF' : '#6B9EC4',
                    border: activeTab === tab?.id ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid transparent',
                    boxShadow: activeTab === tab?.id ? '0 0 10px rgba(0, 212, 255, 0.2)' : 'none'
                  }}
                >
                  <Icon name={tab?.icon} size={20} color="currentColor" />
                  <span className="text-xs font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Region Selector - Australia, UAE & Bangladesh */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => onRegionChange?.('australia')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
              style={{
                background: activeRegion === 'australia' ? 'linear-gradient(135deg, #00D4FF, #0099CC)' : 'rgba(10, 22, 40, 0.8)',
                color: activeRegion === 'australia' ? '#000000' : '#6B9EC4',
                border: activeRegion === 'australia' ? '1px solid rgba(0, 212, 255, 0.6)' : '1px solid rgba(0, 212, 255, 0.2)',
                boxShadow: activeRegion === 'australia' ? '0 0 15px rgba(0, 212, 255, 0.4)' : 'none',
              }}
            >
              <span>🇦🇺</span>
              <span>{language === 'bn' ? 'অস্ট্রেলিয়া' : 'Australia'}</span>
            </button>
            <button
              onClick={() => onRegionChange?.('uae')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
              style={{
                background: activeRegion === 'uae' ? 'linear-gradient(135deg, #00D4FF, #0099CC)' : 'rgba(10, 22, 40, 0.8)',
                color: activeRegion === 'uae' ? '#000000' : '#6B9EC4',
                border: activeRegion === 'uae' ? '1px solid rgba(0, 212, 255, 0.6)' : '1px solid rgba(0, 212, 255, 0.2)',
                boxShadow: activeRegion === 'uae' ? '0 0 15px rgba(0, 212, 255, 0.4)' : 'none',
              }}
            >
              <span>🇦🇪</span>
              <span>{language === 'bn' ? 'UAE' : 'UAE'}</span>
            </button>
            <button
              onClick={() => onRegionChange?.('bangladesh')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
              style={{
                background: activeRegion === 'bangladesh' ? 'linear-gradient(135deg, #00B450, #007A35)' : 'rgba(10, 22, 40, 0.8)',
                color: activeRegion === 'bangladesh' ? '#FFFFFF' : '#6B9EC4',
                border: activeRegion === 'bangladesh' ? '1px solid rgba(0, 180, 80, 0.6)' : '1px solid rgba(0, 180, 80, 0.2)',
                boxShadow: activeRegion === 'bangladesh' ? '0 0 15px rgba(0, 180, 80, 0.4)' : 'none',
              }}
            >
              <span>🇧🇩</span>
              <span>{language === 'bn' ? 'বাংলাদেশ' : 'Bangladesh'}</span>
            </button>
          </div>

          {/* Search Box */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              placeholder={activeRegion === 'uae' ? (language === 'bn' ? 'দুবাই, আবুধাবি, শারজাহ...' : 'Dubai, Abu Dhabi, Sharjah...') : t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="flex-1 px-4 py-3 rounded-lg text-sm outline-none"
              style={{
                background: 'rgba(10, 22, 40, 0.8)',
                border: '1px solid rgba(0, 212, 255, 0.2)',
                color: '#E8F4FF',
                caretColor: '#00D4FF'
              }}
            />
            <button
              type="submit"
              className="px-6 py-3 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all"
              style={{
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#000000',
                boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
              }}
            >
              <Icon name="Search" size={16} color="#000000" />
              {t('search')}
            </button>
          </form>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/mortgage-check')}
              className="px-6 py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all"
              style={{
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#000000',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
            >
              <Icon name="Clock" size={18} color="#000000" />
              {language === 'bn' ? '১০ মিনিটে মর্টগেজ চেক করুন' : 'Get 10-Minute Mortgage Check'}
            </button>
            <button
              onClick={() => navigate('/create-listing')}
              className="px-6 py-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all"
              style={{
                background: 'transparent',
                color: '#00D4FF',
                border: '1px solid rgba(0, 212, 255, 0.4)',
                boxShadow: '0 0 10px rgba(0, 212, 255, 0.1)'
              }}
            >
              <Icon name="Plus" size={18} color="#00D4FF" />
              {language === 'bn' ? 'এআই দিয়ে তালিকা তৈরি করুন' : 'Create a Listing with AI'}
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 pt-4">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle2" size={20} color="#00FF88" />
              <span className="text-sm md:text-base" style={{ color: '#6B9EC4' }}>{language === 'bn' ? 'ক্রেডিট কার্ড লাগবে না' : 'No credit card required'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle2" size={20} color="#00FF88" />
              <span className="text-sm md:text-base" style={{ color: '#6B9EC4' }}>{language === 'bn' ? 'তাৎক্ষণিক এআই বিশ্লেষণ' : 'Instant AI analysis'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle2" size={20} color="#00FF88" />
              <span className="text-sm md:text-base" style={{ color: '#6B9EC4' }}>{language === 'bn' ? 'অস্ট্রেলিয়া ও UAE বাজার' : 'Australia & UAE markets'}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;